// Pending-sync outbox state machine.
//
// One entry per chat records how far the server has acknowledged this chat and
// whether local changes are waiting to be pushed. The chat-sync engine reads
// `shouldAttempt` to decide what to push at a boundary, then folds the attempt
// outcome back in with `reconcileAfterAttempt`. All transitions are pure so the
// retry policy is unit-testable without IndexedDB or the network.
//
// Retry policy (the load-bearing part): NEVER hot-retry a 4xx. A conflict
// waits for an explicit reload; a forbidden/invalid response parks the chat as
// local-only; a deleted response tombstones locally. Only transient failures
// (network / 503 / 429 rate-limit) stay dirty for the next boundary.

import {
    loadOutbox as _loadOutbox,
    getOutboxEntry as _getOutboxEntry,
    putOutboxEntry as _putOutboxEntry,
    mergeOutboxEntry as _mergeOutboxEntry,
    deleteOutboxEntry as _deleteOutboxEntry,
} from "./chatStorage.js";

export { _loadOutbox as loadOutbox, _getOutboxEntry as getOutboxEntry, _putOutboxEntry as putOutboxEntry, _mergeOutboxEntry as mergeOutboxEntry, _deleteOutboxEntry as deleteOutboxEntry };

// A fresh entry for a chat that has never synced. `ackedRevision: 0` means the
// server has no row yet, so the first sync sends expected_revision 0 (create).
export function initialOutbox({ serverChatId, now = 0 }) {
    return {
        serverChatId: serverChatId || null,
        ackedRevision: 0,
        // Server turn_count the last successful sync observed. Drives the
        // rewind tombstone pass: if the chat later shrinks below this, the
        // vacated ordinals are overwritten with tombstones so the server's
        // upsert-only sync can't resurrect the discarded tail. Preserved across
        // transitions by the `...base` spreads below.
        ackedTurnCount: 0,
        dirty: true,
        conflict: false,
        terminal: false,
        terminalKind: null,
        lastError: null,
        attempts: 0,
        // Earliest wall-clock (ms) the next transient-failure retry may fire.
        // Set from the backoff schedule when a sync fails transiently; 0 means
        // no backoff pending. Kept separate from `updatedAt` so a later edit's
        // `markDirty` can't reset the failure clock. Preserved by `...base`.
        nextAttemptAt: 0,
        updatedAt: now,
        // content-hash → { blobId, bytes, contentType } for already-finalized
        // externalized blobs, so an unchanged leaf isn't re-uploaded every
        // boundary. Pruned when the server reports an eviction. Preserved
        // across transitions by the `...base` spreads below.
        blobHashes: {},
        // turn_ordinal → content fingerprint of the last successfully-synced
        // turn payload. Drives delta sync: a boundary POSTs only turns whose
        // fingerprint changed since the last ack (plus the head and any rewind
        // tombstones), so an unchanged transcript isn't re-upserted every
        // boundary. Preserved across transitions by the `...base` spreads.
        turnHashes: {},
        // conversation key → newest exchange timestamp the server has acked.
        // Capture's equivalent of `turnHashes`: a boundary pushes only LLM calls
        // newer than the cursor, so a long chat re-sends its whole history
        // exactly once per call rather than once per boundary. Advanced only
        // over exchanges a POST actually carried, so a budget-deferred exchange
        // goes out on the next one. Preserved across transitions by the
        // `...base` spreads.
        exchangeCursors: {},
        // Wall-clock floor (ms) under which no LLM call is ever published from
        // this device. Set when a chat is RESTORED: everything the server holds
        // at that moment is already captured, so re-deriving and re-sending it
        // is pure waste — and a restored chat carries no agent messages, so
        // per-conversation cursors cannot be derived at restore time (the
        // conversation keys aren't knowable yet). A single floor needs no such
        // knowledge. `exchangeCursors` still does the per-conversation work
        // once the chat is live. Preserved across transitions by `...base`.
        capturedThrough: 0,
        // `ackedRevision` this entry's conflict park was last probed at by the
        // engine's mid-session recovery. Bounds that probe to one server
        // transcript fetch per park: a park the recovery declines to resolve
        // (genuine divergence, which only a reload adopts) would otherwise
        // re-fetch on every save boundary for the rest of the session.
        // Preserved across transitions by the `...base` spreads.
        conflictProbedRevision: null,
        // Idempotency nonce for the in-flight logical sync + a signature of the
        // exact bytes it was minted for. Reused across retries of an unchanged
        // write (lost-ack → server ACKs idempotently, no spurious 409); any
        // content/revision change mints a fresh nonce; cleared on a landed sync.
        // Null when no sync is pending. Preserved across transitions by `...base`.
        syncNonce: null,
        syncNonceSig: null,
    };
}

// Mark local changes present. Clears a prior terminal park (a new edit is a
// fresh reason to try) but NOT a conflict — a conflict must be resolved by a
// reload, not papered over by continuing to write. `deleted` terminals stick.
export function markDirty(entry, { now = 0 } = {}) {
    const base = entry || initialOutbox({ serverChatId: null, now });
    return {
        ...base,
        dirty: true,
        terminal: base.terminalKind === "deleted" ? base.terminal : false,
        terminalKind: base.terminalKind === "deleted" ? "deleted" : null,
        updatedAt: now,
    };
}

// Transient-failure retry backoff: exponential from 2s, capped at 5 min
// (attempts 1→2s, 2→4s, 3→8s … ≥9→300s). Pure/deterministic — no jitter here:
// the per-user-action sync boundaries are already desynchronized across tabs,
// and the app-open drain adds its own scheduling jitter.
const RETRY_BASE_MS = 2000;
const RETRY_CAP_MS = 5 * 60 * 1000;
export function retryBackoffMs(attempts) {
    const n = Math.max(0, (attempts | 0) - 1);
    return Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** n);
}

// The engine pushes a chat only when it's dirty, not in conflict, not parked
// terminal, and not inside a transient-failure backoff window. The backoff gate
// is enforced only when the caller supplies a clock (`now`); with no clock it
// degrades to the plain dirty/park check (back-compat for pure-state tests).
export function shouldAttempt(entry, { now = 0 } = {}) {
    if (!entry) return false;
    if (!entry.dirty || entry.conflict || entry.terminal) return false;
    if (now && entry.nextAttemptAt && now < entry.nextAttemptAt) return false;
    return true;
}

// The `expected_revision` to send: whatever the server last acked (0 creates).
export function expectedRevision(entry) {
    return entry?.ackedRevision ?? 0;
}

// Fold an attempt outcome back into the entry. `outcome` is one of:
//   { ok: true, revision }
//   { ok: false, kind, currentRevision? }   // kind from ChatSyncError
// Returns the next entry; the engine persists it and, for a "deleted" outcome,
// also drops the local chat.
export function reconcileAfterAttempt(entry, outcome, { now = 0 } = {}) {
    const base = entry || initialOutbox({ serverChatId: null, now });
    if (outcome?.ok) {
        return {
            ...base,
            ackedRevision: Number(outcome.revision ?? base.ackedRevision),
            dirty: false,
            conflict: false,
            terminal: false,
            terminalKind: null,
            lastError: null,
            attempts: 0,
            nextAttemptAt: 0,
            updatedAt: now,
        };
    }
    const kind = outcome?.kind || "network";
    switch (kind) {
        case "conflict":
            // Another writer advanced the revision. Adopt it so a later forced
            // re-sync (after reload) has the right base, but park until the UI
            // resolves — never auto-clobber the other writer (LWW is decided by
            // the reload affordance, not a blind overwrite here).
            return {
                ...base,
                ackedRevision: Number(outcome.currentRevision ?? base.ackedRevision),
                conflict: true,
                lastError: "chat updated elsewhere — reload",
                updatedAt: now,
            };
        case "not_found":
            // Server has no such row (e.g. tombstone GC, or we drifted). The next
            // boundary must RECREATE it from scratch, so reset to a fresh outbox
            // (keeping only serverChatId): clearing ackedRevision alone is not
            // enough — the delta caches (turnHashes, blobHashes, ackedTurnCount)
            // describe a server row that no longer exists, so preserving them
            // would make the recreate POST send an empty transcript (every turn
            // reads "unchanged" against the stale fingerprints) and the chat would
            // be recreated with no turns. A clean slate re-sends the whole body.
            return { ...initialOutbox({ serverChatId: base.serverChatId, now }), lastError: "not_found" };
        case "deleted":
            // Tombstoned elsewhere. Park terminal + signal the engine to drop
            // the local copy.
            return { ...base, terminal: true, terminalKind: "deleted", lastError: "chat deleted elsewhere", updatedAt: now };
        case "forbidden":
            // No write permission (e.g. Viewer role). Degrade to local-only
            // silently; do NOT hot-retry. Cleared next session in case the
            // grant changes.
            return { ...base, terminal: true, terminalKind: "forbidden", lastError: "no write access — local only", updatedAt: now };
        case "invalid":
            return { ...base, terminal: true, terminalKind: "invalid", lastError: outcome.message || "invalid sync", updatedAt: now };
        case "quota":
            // Per-user chat-count cap reached: a retry can't help until the user
            // frees a slot, so park terminal (no hot-loop) + surface it. A later
            // edit (markDirty) or a new session (resetForSession) retries in case
            // they've since deleted chats.
            return { ...base, terminal: true, terminalKind: "quota", lastError: "chat backup limit reached", updatedAt: now };
        default: {
            // Transient (network / busy / rate-limit): stay dirty and set the
            // backoff deadline so the next boundary doesn't immediately re-POST
            // into a downstream that's already degrading.
            const attempts = (base.attempts || 0) + 1;
            return { ...base, dirty: true, attempts, nextAttemptAt: now + retryBackoffMs(attempts), lastError: kind, updatedAt: now };
        }
    }
}

// App-open reset: give parked-forbidden/invalid chats one more chance this
// session (a re-login or flag flip may have changed things). Does NOT touch
// ackedRevision or dirty. A `conflict` park is PRESERVED — it must only be
// cleared by an explicit adopt-server step (the engine re-fetches the newer
// server transcript on reload); clearing it here while the entry is still
// dirty would let the next drain re-POST the stale local copy at the adopted
// revision and silently overwrite the other device's newer content. `deleted`
// terminals are likewise preserved (the chat is gone server-side).
export function resetForSession(entry, { now = 0 } = {}) {
    if (!entry) return entry;
    if (entry.terminalKind === "deleted" || entry.conflict) return entry;
    return {
        ...entry,
        terminal: false,
        terminalKind: null,
        attempts: 0,
        nextAttemptAt: 0,
        lastError: null,
        updatedAt: now,
    };
}
